import React, { useState, useEffect } from 'react';
import { DailyRecord } from './types';
import { getDailyRecord, saveDailyRecord, loadAppState, saveAppState, subscribeToDailyRecord, syncDailyRecordFromCloud, syncAllDailyRecordsFromCloud, syncSettingsFromCloud } from './lib/storage';
import { syncLearningDataFromCloud, subscribeToLearningData, clearAllLearningData } from './lib/learningStorage';
import { fetchCloudFileRecords, subscribeToCloudFiles, clearAllCloudFiles } from './lib/cloudFileStorage';
import { syncNewsDataFromCloud, subscribeToNewsData } from './lib/newsStorage';
import { notifyPlannerChanged } from './lib/plannerClient';

// Worker REST API Auth & Database
import { getStoredUser, setStoredUser, apiGet, apiPost, apiPut } from './lib/api';

// Module Components
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import RehabModule from './components/RehabModule';
import BellyFatModule from './components/BellyFatModule';
import NutritionModule from './components/NutritionModule';
import WeightModule from './components/WeightModule';
import SwedishModule from './components/SwedishModule';
import WeeklyRoutineModule from './components/WeeklyRoutineModule';
import ActivityLogModule from './components/ActivityLogModule';
import NotesModule from './components/NotesModule';
import ReportsModule from './components/ReportsModule';
import BrandingModule from './components/BrandingModule';
import SettingsModule from './components/SettingsModule';
import { AppLogo } from './components/AppLogo';
import UsersAdminModule from './components/UsersAdminModule';
import LearningModule from './components/LearningModule';
import NewsModule from './components/NewsModule';
import ShoppingModule from './components/ShoppingModule';
import { UserAvatar } from './components/UserAvatar';
import ReminderModule from './components/ReminderModule';
import SeasonalMealPlanModule from './components/SeasonalMealPlanModule';
import PersonalDoctorModule from './components/PersonalDoctorModule';
import FinanceModule from './components/FinanceModule';
import DictionaryReferenceModule from './components/DictionaryReferenceModule';
import { LanguageLearningModule } from './components/LanguageLearningModule';
import VersionHistoryModal from './components/VersionHistoryModal';
import { CURRENT_VERSION, isUserAdmin } from './lib/versionHistory';
import { syncShoppingDataFromCloud, subscribeToShoppingData } from './lib/shoppingStorage';
import { getAppLanguage, setAppLanguage, Language, translate, LanguageContext, applyLanguageToDocument } from './lib/i18n';

// Icons
import { 
  Home, Activity, Flame, Apple, Scale, BookOpen, Calendar, ClipboardList, 
  FileEdit, FileBarChart, Sparkles, Bell, Menu, X, ChevronRight, ChevronDown, Folder, Moon, Sun, LogOut, RefreshCw, Palette, Users, GraduationCap, Newspaper, ShoppingBag, Utensils, HeartPulse, Stethoscope, Wallet, Globe,
  WifiOff, Database, ShieldAlert
} from 'lucide-react';

export default function App() {
  // Get initial date in local YYYY-MM-DD format
  const getLocalDateString = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - (offset * 60 * 1000));
    return local.toISOString().split('T')[0];
  };

  const [currentDate, setCurrentDate] = useState<string>(getLocalDateString());
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [record, setRecord] = useState<DailyRecord>(getDailyRecord(currentDate));
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [themeAccent, setThemeAccent] = useState<string>('indigo');
  const [currentLang, setCurrentLang] = useState<Language>(getAppLanguage());

  useEffect(() => {
    applyLanguageToDocument(currentLang);
    const handleLangEvent = (e: any) => {
      if (e.detail?.lang) {
        setCurrentLang(e.detail.lang);
      }
    };
    window.addEventListener('app-language-changed', handleLangEvent);
    return () => window.removeEventListener('app-language-changed', handleLangEvent);
  }, [currentLang]);

  const handleSelectLanguage = (newLang: Language) => {
    setAppLanguage(newLang);
    setCurrentLang(newLang);
    const currentState = loadAppState();
    const updatedState = {
      ...currentState,
      settings: {
        ...currentState.settings,
        language: newLang
      }
    };
    saveAppState(updatedState);
  };

  // Collapsible menu categories state
  const [expandedCategories, setExpandedCategories] = useState<Record<number, boolean>>({
    0: true,
    1: true,
    2: true,
    3: true
  });

  const handleThemeAccentChange = (accent: string) => {
    setThemeAccent(accent);
  };

  // Authentication & Network states
  const [user, setUser] = useState<any>(() => getStoredUser());
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [dbLoading, setDbLoading] = useState<boolean>(false);
  const [showVersionModal, setShowVersionModal] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [checkingConnection, setCheckingConnection] = useState<boolean>(false);

  const isAdmin = isUserAdmin(user?.email);

  // Connection & Database Reachability Verifier
  const verifyDatabaseConnection = async () => {
    setCheckingConnection(true);
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        setIsOffline(true);
        return false;
      }
      // Verify active connectivity by fetching health check or cloud settings
      const res = await fetch('/api/health', { method: 'GET', cache: 'no-store' }).catch(() => null);
      if (!res || !res.ok) {
        setIsOffline(true);
        return false;
      }
      setIsOffline(false);
      // Reload all data directly from Database
      syncSettingsFromCloud().catch(() => {});
      syncAllDailyRecordsFromCloud().catch(() => {});
      syncLearningDataFromCloud().catch(() => {});
      fetchCloudFileRecords().catch(() => {});
      return true;
    } catch (err) {
      setIsOffline(true);
      return false;
    } finally {
      setCheckingConnection(false);
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      verifyDatabaseConnection();
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsOffline(true);
    } else {
      verifyDatabaseConnection();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen to Auth state and handle Google OAuth Callback redirect
  useEffect(() => {
    // Check URL parameters for Google OAuth callback (?login=success&userId=xxx&email=yyy)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('login') === 'success') {
      const userId = urlParams.get('userId');
      const email = urlParams.get('email');
      if (userId && email) {
        const u = { id: userId, email: decodeURIComponent(email), displayName: decodeURIComponent(email.split('@')[0]) };
        setStoredUser(u);
        setUser(u);
        // Clean URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Initial sync of cloud settings, files, learning data and all daily records
  useEffect(() => {
    const userId = user?.id || user?.uid;
    Promise.all([
      syncSettingsFromCloud(userId),
      fetchCloudFileRecords(),
      syncLearningDataFromCloud(),
      syncAllDailyRecordsFromCloud()
    ]).then(() => {
      notifyPlannerChanged();
    }).catch(err => console.warn('Initial sync notice:', err));
  }, [user?.id]);

  // Weekly Schedule Notification Alarm Trigger Engine
  useEffect(() => {
    const triggeredSet = new Set<string>();

    const checkWeeklyNotifications = () => {
      try {
        if (!('Notification' in window) || Notification.permission !== 'granted') {
          return;
        }

        const appState = loadAppState();
        const weeklyRoutine = appState.settings.weeklyRoutine || [];
        if (weeklyRoutine.length === 0) return;

        const now = new Date();
        const currentDay = now.getDay(); // 0 = Sun, 1 = Mon ...
        const currentHours = String(now.getHours()).padStart(2, '0');
        const currentMins = String(now.getMinutes()).padStart(2, '0');
        const currentTimeStr = `${currentHours}:${currentMins}`;

        weeklyRoutine.forEach((task) => {
          if (task.phoneNotificationEnabled === false) return;
          if (Number(task.dayOfWeek) !== currentDay) return;
          if (task.time === currentTimeStr) {
            const triggerKey = `${currentDay}-${currentTimeStr}-${task.id}`;
            if (!triggeredSet.has(triggerKey)) {
              triggeredSet.add(triggerKey);
              new Notification(translate(currentLang, 'appWeeklyReminderTitle', undefined, { title: task.title }), {
                body: task.description || translate(currentLang, 'appWeeklyReminderBody', undefined, { title: task.title })
              });
            }
          }
        });
      } catch (e) {
        // Silently catch background notification errors
      }
    };

    const interval = setInterval(checkWeeklyNotifications, 30000);
    checkWeeklyNotifications();
    return () => clearInterval(interval);
  }, []);

  // Subscribe to local and cloud updates for DailyRecord
  useEffect(() => {
    const unsubscribe = subscribeToDailyRecord((updatedRecord) => {
      setRecord(updatedRecord);
    });
    return () => unsubscribe();
  }, []);

  // Sync daily records on login, date change, or tab change via D1
  useEffect(() => {
    const userId = user?.id || user?.uid;
    if (!userId) {
      setRecord(getDailyRecord(currentDate));
      return;
    }

    setDbLoading(true);
    syncDailyRecordFromCloud(currentDate)
      .then(cloudRecord => {
        if (!cloudRecord) {
          setRecord(getDailyRecord(currentDate));
        }
      })
      .catch(err => {
        console.warn('D1 daily record fetch error:', err);
        setRecord(getDailyRecord(currentDate));
      })
      .finally(() => setDbLoading(false));
  }, [currentDate, user, activeTab]);

  // Real-time multi-device sync: poll D1 every 8s & sync on window focus/tab return
  useEffect(() => {
    const userId = user?.id || user?.uid;
    if (!userId) return;

    const performSync = () => {
      if (document.visibilityState === 'visible') {
        syncDailyRecordFromCloud(currentDate).catch(() => {});
      }
    };

    // Polling interval
    const interval = setInterval(performSync, 8000);

    // Window focus / tab switch listeners
    window.addEventListener('focus', performSync);
    document.addEventListener('visibilitychange', performSync);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', performSync);
      document.removeEventListener('visibilitychange', performSync);
    };
  }, [user, currentDate]);

  // Sync settings and subscribe to all modules on login
  useEffect(() => {
    const userId = user?.id || user?.uid;
    if (!userId) return;

    // Load settings from local REST API and apply
    apiGet<any>('/api/settings')
      .then(res => {
        if (res.success && res.data) {
          const cloudSettings = res.data;
          const currentState = loadAppState();
          const updatedState = {
            ...currentState,
            settings: {
              ...currentState.settings,
              theme: cloudSettings.theme || currentState.settings.theme,
              language: cloudSettings.language || currentState.settings.language,
              calendarType: cloudSettings.calendar_type || currentState.settings.calendarType,
              height: cloudSettings.height_cm ?? cloudSettings.height ?? cloudSettings.settings?.height ?? currentState.settings.height,
              currentWeight: cloudSettings.current_weight ?? cloudSettings.currentWeight ?? cloudSettings.settings?.currentWeight ?? currentState.settings.currentWeight,
              weightGoal: cloudSettings.target_weight_kg ?? cloudSettings.weightGoal ?? cloudSettings.settings?.weightGoal ?? currentState.settings.weightGoal,
              bodyFatGoal: cloudSettings.target_body_fat_pct ?? cloudSettings.bodyFatGoal ?? cloudSettings.settings?.bodyFatGoal ?? currentState.settings.bodyFatGoal,
            }
          };
          saveAppState(updatedState, false);
          setRecord(getDailyRecord(currentDate));
        } else {
          const currentState = loadAppState();
          apiPut('/api/settings', {
            theme: currentState.settings.theme,
            language: currentState.settings.language,
            calendar_type: currentState.settings.calendarType,
            notifications_enabled: 1
          }).catch(err => console.warn('Initial settings upload error:', err));
        }
      })
      .catch(err => console.warn('Local REST settings fetch error:', err));

    // Subscribe to learning assistant topics & sessions
    const unsubscribeLearning = subscribeToLearningData(() => {
      // Triggers component updates
    });

    // Subscribe to cloud shared files
    const unsubscribeFiles = subscribeToCloudFiles(() => {
      // Triggers component updates
    });

    // Subscribe to news radar data across all devices
    const unsubscribeNews = subscribeToNewsData(() => {
      // Triggers component updates
    });

    // Subscribe to shopping list data across all devices
    const unsubscribeShopping = subscribeToShoppingData(() => {
      // Triggers component updates
    });

    // Initial fetch helper for offline-to-online transition
    syncLearningDataFromCloud().catch(err => console.warn(err));
    fetchCloudFileRecords().catch(err => console.warn(err));
    syncNewsDataFromCloud().catch(err => console.warn(err));
    syncShoppingDataFromCloud().catch(err => console.warn(err));

    return () => {
      unsubscribeLearning();
      unsubscribeFiles();
      unsubscribeNews();
      unsubscribeShopping();
    };
  }, [user, currentDate]);

  // Handle dark mode side effects
  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Track user activity & page visits via D1
  useEffect(() => {
    if (!user) return;

    const recordUserTracking = async () => {
      try {
        const userId = user.id || user.uid;
        const nowStr = new Date().toISOString();
        const derivedName = (user.displayName && user.displayName !== translate(currentLang, 'defaultUserApp') && user.displayName !== translate(currentLang, 'appShortName') && user.displayName !== translate(currentLang, 'defaultUser'))
          ? user.displayName 
          : user.email ? user.email.split('@')[0] : translate(currentLang, 'defaultUser');

        const userPhoto = user.photoURL || user.photo_url || '';

        await apiPost('/api/activity-logs', {
          action: `visit:${activeTab}`,
          details: {
            page: activeTab,
            email: user.email || '',
            displayName: derivedName,
            photoURL: userPhoto
          }
        });
      } catch (error) {
        console.warn('Failed to save tracking record:', error);
      }
    };

    recordUserTracking();
  }, [user, activeTab]);

  // Save changes to in-memory state and D1
  const handleUpdateRecord = async (updated: DailyRecord) => {
    setRecord(updated);
    saveDailyRecord(currentDate, updated);
  };

  // Re-trigger states when custom profile settings updated
  const handleSettingsUpdated = () => {
    setRecord(getDailyRecord(currentDate));
  };

  const menuCategories = [
    {
      title: translate(currentLang, 'catMain', 'اصلی و دستیاران'),
      items: [
        { id: 'dashboard', label: translate(currentLang, 'dashboard', 'داشبورد سلامت'), icon: Home, color: 'text-indigo-500' },
        { id: 'finance', label: translate(currentLang, 'finance', 'مدیریت مالی و فاکتورها (AI)'), icon: Wallet, color: 'text-emerald-500' },
        { id: 'reminders', label: translate(currentLang, 'reminders', 'دفترچه یادآور خانوادگی'), icon: Bell, color: 'text-rose-500' },
        { id: 'shopping', label: translate(currentLang, 'shopping', 'لیست خرید هوشمند'), icon: ShoppingBag, color: 'text-teal-500' },
        { id: 'news', label: translate(currentLang, 'news', 'رادار اخبار و هوش مصنوعی'), icon: Newspaper, color: 'text-sky-500' },
        { id: 'learning-assistant', label: translate(currentLang, 'learningAssistant', 'دستیار یادگیری'), icon: GraduationCap, color: 'text-indigo-500' },
      ]
    },
    {
      title: translate(currentLang, 'catHealth', 'سلامت، تغذیه و ورزشی'),
      items: [
        { id: 'personal-doctor', label: translate(currentLang, 'personalDoctor', 'پزشک اختصاصی (AI)'), icon: Stethoscope, color: 'text-teal-500' },
        { id: 'nutrition', label: translate(currentLang, 'nutrition', 'ثبت و پیگیری تغذیه'), icon: Apple, color: 'text-emerald-600' },
        { id: 'seasonal-meal-plan', label: translate(currentLang, 'seasonalMealPlan', 'برنامه غذایی فصلی (GLP-1)'), icon: Utensils, color: 'text-amber-500' },
        { id: 'rehab', label: translate(currentLang, 'rehabTitle', 'فیزیوتراپی و تمرینات اصلاحی'), icon: Activity, color: 'text-emerald-500' },
        { id: 'weight-tracker', label: translate(currentLang, 'weightTracker', 'پایش وزن و شاخص BMI'), icon: Scale, color: 'text-indigo-500' },
      ]
    },
    {
      title: translate(currentLang, 'catPlanning', 'برنامه‌ریزی، یادگیری و گزارش'),
      items: [
        { id: 'language-learning', label: translate(currentLang, 'languageLearningTitle', 'آموزش زبان'), icon: Globe, color: 'text-emerald-500' },
        { id: 'schedules', label: translate(currentLang, 'schedules', 'برنامه‌ریزی هفتگی'), icon: Calendar, color: 'text-indigo-600' },
        { id: 'swedish', label: translate(currentLang, 'swedish', 'یادگیری زبان سوئدی'), icon: BookOpen, color: 'text-yellow-500' },
        { id: 'activities', label: translate(currentLang, 'activities', 'ثبت فعالیت‌های متفرقه'), icon: ClipboardList, color: 'text-pink-500' },
        { id: 'notes', label: translate(currentLang, 'notes', 'یادداشت‌ها و خلق‌و‌خو'), icon: FileEdit, color: 'text-amber-500' },
        { id: 'reports', label: translate(currentLang, 'reports', 'گزارش‌گیری و خروجی AI'), icon: FileBarChart, color: 'text-indigo-400' },
      ]
    },
    {
      title: translate(currentLang, 'catSettings', 'تنظیمات و مدیریت'),
      items: [
        { id: 'branding', label: translate(currentLang, 'branding', 'پوسته و رنگ‌های برنامه'), icon: Palette, color: 'text-purple-500' },
        { id: 'settings', label: translate(currentLang, 'settings', 'تنظیمات و همگام‌سازی'), icon: Bell, color: 'text-slate-500' },
        ...(isAdmin ? [
          { id: 'dictionary', label: translate(currentLang, 'dictionaryTab', 'مرجع متغیرها و واژه‌نامه'), icon: Globe, color: 'text-teal-500' },
          { id: 'users-admin', label: translate(currentLang, 'usersAdmin', 'مدیریت و پایش کاربران'), icon: Users, color: 'text-indigo-600' }
        ] : [])
      ]
    }
  ];

  // Auto-expand category containing active tab
  useEffect(() => {
    const activeCatIndex = menuCategories.findIndex(cat => 
      cat.items.some(item => item.id === activeTab)
    );
    if (activeCatIndex !== -1) {
      setExpandedCategories(prev => ({ ...prev, [activeCatIndex]: true }));
    }
  }, [activeTab]);

  const renderActiveContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard 
            currentDate={currentDate} 
            onSetDate={setCurrentDate} 
            record={record} 
            onUpdateRecord={handleUpdateRecord} 
            onNavigateTab={setActiveTab}
            isDarkMode={isDarkMode}
            onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
            themeAccent={themeAccent}
          />
        );
      case 'finance':
        return <FinanceModule />;
      case 'reminders':
        return <ReminderModule currentDate={currentDate} onSetDate={setCurrentDate} />;
      case 'news':
        return <NewsModule />;
      case 'shopping':
        return <ShoppingModule />;
      case 'learning-assistant':
        return <LearningModule />;
      case 'rehab':
      case 'belly-fat':
        return <RehabModule record={record} onUpdateRecord={handleUpdateRecord} themeAccent={themeAccent} />;
      case 'nutrition':
        return (
          <NutritionModule 
            record={record} 
            onUpdateRecord={handleUpdateRecord} 
            currentDate={currentDate}
            onSetDate={setCurrentDate}
          />
        );
      case 'personal-doctor':
        return <PersonalDoctorModule record={record} onNavigateToTab={setActiveTab} />;
      case 'seasonal-meal-plan':
        return (
          <SeasonalMealPlanModule 
            record={record} 
            onUpdateRecord={handleUpdateRecord} 
            onNavigateToShopping={() => setActiveTab('shopping')} 
          />
        );
      case 'weight-tracker':
        return <WeightModule record={record} onUpdateRecord={handleUpdateRecord} />;
      case 'language-learning':
        return <LanguageLearningModule />;
      case 'swedish':
        return <SwedishModule record={record} onUpdateRecord={handleUpdateRecord} />;
      case 'weekly-routine':
      case 'schedules':
        return <WeeklyRoutineModule record={record} onUpdateRecord={handleUpdateRecord} user={user} />;
      case 'activities':
        return <ActivityLogModule record={record} onUpdateRecord={handleUpdateRecord} />;
      case 'notes':
        return <NotesModule record={record} onUpdateRecord={handleUpdateRecord} />;
      case 'reports':
        return <ReportsModule currentDate={currentDate} record={record} />;
      case 'branding':
        return <BrandingModule themeAccent={themeAccent} onThemeAccentChange={handleThemeAccentChange} />;
      case 'dictionary':
        return isAdmin ? <DictionaryReferenceModule /> : (
          <Dashboard 
            currentDate={currentDate} 
            onSetDate={setCurrentDate} 
            record={record} 
            onUpdateRecord={handleUpdateRecord} 
            onNavigateTab={setActiveTab}
            isDarkMode={isDarkMode}
            onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
            themeAccent={themeAccent}
          />
        );
      case 'users-admin':
        return isAdmin ? <UsersAdminModule /> : (
          <Dashboard 
            currentDate={currentDate} 
            onSetDate={setCurrentDate} 
            record={record} 
            onUpdateRecord={handleUpdateRecord} 
            onNavigateTab={setActiveTab}
            isDarkMode={isDarkMode}
            onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
            themeAccent={themeAccent}
          />
        );
      case 'settings':
        return <SettingsModule onSettingsUpdated={handleSettingsUpdated} onNavigateToSchedules={() => setActiveTab('schedules')} />;
      default:
        return <div className="text-gray-500 text-sm">{translate(currentLang, 'appComponentStandby')}</div>;
    }
  };

  if (isOffline) {
    return (
      <LanguageContext.Provider value={{
        lang: currentLang,
        setLang: handleSelectLanguage,
        t: (key: string, defaultTextOrParams?: string | Record<string, string | number>, params?: Record<string, string | number>) => 
          translate(currentLang, key, defaultTextOrParams, params),
        dir: currentLang === 'fa' ? 'rtl' : 'ltr'
      }}>
        <div dir={currentLang === 'fa' ? 'rtl' : 'ltr'} className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${isDarkMode ? 'bg-gray-950 text-white' : 'bg-slate-50 text-slate-900'} font-sans`}>
          <div className={`max-w-md w-full rounded-3xl p-6 sm:p-8 border shadow-2xl text-center space-y-6 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-slate-200'}`}>
            <div className="w-20 h-20 mx-auto rounded-3xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 shadow-inner">
              <WifiOff className="w-10 h-10 animate-pulse" />
            </div>

            <div className="space-y-2">
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-amber-600 dark:text-amber-400">
                {translate(currentLang, 'offlineTitle', 'عدم اتصال به اینترنت')}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
                {translate(currentLang, 'offlineDesc', 'برنامه «پیشرفت» تمامی داده‌ها، تنظیمات و یادآورها را مستقیماً از پایگاه داده ابری دریافت می‌کند. به دلیل عدم اتکا به حافظه محلی (LocalStorage)، جهت امنیت و یکپارچگی اطلاعات، اتصال فعال به اینترنت الزامی است.')}
              </p>
            </div>

            <div className={`p-4 rounded-2xl border text-xs text-right space-y-2.5 ${isDarkMode ? 'bg-gray-950/60 border-gray-800 text-slate-300' : 'bg-slate-100/80 border-slate-200 text-slate-700'}`}>
              <div className="flex items-center gap-2 font-bold text-amber-700 dark:text-amber-300">
                <Database className="w-4 h-4 shrink-0 text-amber-500" />
                <span>دسترسی مستقیم و انحصاری به پایگاه داده ابری:</span>
              </div>
              <ul className="list-disc list-inside space-y-1.5 text-[11px] opacity-90 pr-1">
                <li>هیچ داده یا تنظیماتی از حافظه محلی (LocalStorage) بارگذاری نمی‌شود.</li>
                <li>اطلاعات شما ۱۰۰٪ با پایگاه داده ابری همگام‌سازی مستقیم دارند.</li>
                <li>لطفاً اتصال اینترنت، Wi-Fi یا دیتای همراه گوشی خود را وصل نمایید.</li>
              </ul>
            </div>

            <button
              onClick={verifyDatabaseConnection}
              disabled={checkingConnection}
              className="w-full py-3.5 px-6 rounded-2xl bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-bold text-sm shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${checkingConnection ? 'animate-spin' : ''}`} />
              <span>
                {checkingConnection
                  ? translate(currentLang, 'checkingConn', 'در حال بررسی اتصال به پایگاه داده...')
                  : translate(currentLang, 'retryConn', 'تلاش مجدد برای برقراری اتصال')}
              </span>
            </button>
          </div>
        </div>
      </LanguageContext.Provider>
    );
  }

  if (authLoading) {
    return (
      <div dir={currentLang === 'fa' ? 'rtl' : 'ltr'} className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-gray-950 font-sans">
        <div className="flex flex-col items-center gap-4 animate-pulse">
          <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
          <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">{translate(currentLang, 'appCheckingAccount')}</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onSuccess={(u) => { setStoredUser(u); setUser(u); }} onSkip={() => {}} />;
  }

  return (
    <LanguageContext.Provider value={{
      lang: currentLang,
      setLang: handleSelectLanguage,
      t: (key: string, defaultTextOrParams?: string | Record<string, string | number>, params?: Record<string, string | number>) => 
        translate(currentLang, key, defaultTextOrParams, params),
      dir: currentLang === 'fa' ? 'rtl' : 'ltr'
    }}>
      <div dir={currentLang === 'fa' ? 'rtl' : 'ltr'} className={`min-h-screen bg-slate-50/50 text-gray-800 transition-colors duration-200 dark:bg-gray-950 dark:text-gray-100 font-sans`}>
      <style>{`
        :root {
          ${themeAccent !== 'indigo' ? `
            --color-indigo-50: var(--color-${themeAccent}-50);
            --color-indigo-100: var(--color-${themeAccent}-100);
            --color-indigo-200: var(--color-${themeAccent}-200);
            --color-indigo-300: var(--color-${themeAccent}-300);
            --color-indigo-400: var(--color-${themeAccent}-400);
            --color-indigo-500: var(--color-${themeAccent}-500);
            --color-indigo-600: var(--color-${themeAccent}-600);
            --color-indigo-700: var(--color-${themeAccent}-700);
            --color-indigo-800: var(--color-${themeAccent}-800);
            --color-indigo-900: var(--color-${themeAccent}-900);
            --color-indigo-950: var(--color-${themeAccent}-950);
          ` : ''}
        }
        
        /* iOS Safe Area Helper Styles */
        .ios-safe-header {
          padding-top: calc(0.75rem + env(safe-area-inset-top, 0px)) !important;
        }
        .ios-safe-drawer-top {
          padding-top: calc(1.25rem + env(safe-area-inset-top, 0px)) !important;
        }
        .ios-safe-drawer-bottom {
          padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px)) !important;
        }
        .ios-safe-bottom-nav {
          padding-bottom: calc(0.65rem + env(safe-area-inset-bottom, 0px)) !important;
        }
      `}</style>
      
      {/* Mobile Top Header */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 border-b border-gray-100 dark:border-gray-800/80 px-4 py-3 ios-safe-header flex items-center justify-between backdrop-blur">
        <div className="flex items-center gap-2">
          {/* Brand Logo */}
          <AppLogo className="w-9 h-9" />
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-base font-display tracking-tight text-gray-900 dark:text-white">{translate(currentLang, 'appShortName')}</span>
              <button
                onClick={() => {
                  if (isAdmin) setShowVersionModal(true);
                }}
                className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 transition-all ${
                  isAdmin 
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 hover:scale-105 cursor-pointer border border-emerald-300/80 dark:border-emerald-800' 
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                }`}
                title={isAdmin ? translate(currentLang, 'appAdminVersionList', 'مشاهده لیست تغییرات نسخه‌ها (مخصوص مدیریت)') : translate(currentLang, 'appVersionPrefix', '', { version: CURRENT_VERSION })}
              >
                <span>v{CURRENT_VERSION}</span>
                {isAdmin && <Sparkles className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />}
              </button>
            </div>
            <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-medium leading-none mt-0.5">{translate(currentLang, 'appVersionOnly', '', { version: CURRENT_VERSION })} &bull; {translate(currentLang, 'appCloudDb')}</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Quick Language Selector */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 border border-gray-200/60 dark:border-gray-700/60">
            <button
              onClick={() => handleSelectLanguage('fa')}
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-all ${
                currentLang === 'fa'
                  ? 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white shadow-xs'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="فارسی"
            >
              🇮🇷 FA
            </button>
            <button
              onClick={() => handleSelectLanguage('en')}
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-all ${
                currentLang === 'en'
                  ? 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white shadow-xs'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="English"
            >
              🇬🇧 EN
            </button>
            <button
              onClick={() => handleSelectLanguage('sv')}
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-all ${
                currentLang === 'sv'
                  ? 'bg-white dark:bg-indigo-600 text-indigo-600 dark:text-white shadow-xs'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Svenska"
            >
              🇸🇪 SV
            </button>
          </div>

          <button 
            onClick={() => setIsDarkMode(!isDarkMode)} 
            className="p-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Main Container */}
      <div className="flex relative">
        
        {/* Desktop Sidebar Navigation */}
        <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800/80 sticky top-0 h-screen select-none shrink-0 p-5 justify-between">
          <div className="space-y-6">
            
            {/* Logo details */}
            <div className="flex items-center gap-3 px-1.5">
              <AppLogo className="w-10 h-10" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-extrabold text-lg font-display tracking-tight text-gray-900 dark:text-white block">
                    {translate(currentLang, 'appShortName', 'پیشرفت')}
                  </span>
                  <button
                    onClick={() => {
                      if (isAdmin) setShowVersionModal(true);
                    }}
                    className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-md transition-all ${
                      isAdmin 
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900 cursor-pointer border border-emerald-200 dark:border-emerald-800' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                    }`}
                    title={isAdmin ? translate(currentLang, 'appAdminVersionListCustom') : translate(currentLang, 'appVersionPrefix', undefined, { version: CURRENT_VERSION })}
                  >
                    v{CURRENT_VERSION}
                  </button>
                </div>
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold block leading-tight mt-0.5 truncate">
                  v{CURRENT_VERSION} &bull; {translate(currentLang, 'synced', 'همگام ابری')}
                </span>
              </div>
            </div>

            {/* Desktop Language Selector Bar */}
            <div className="bg-gray-50 dark:bg-gray-800/60 p-1 rounded-xl border border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-400 px-2 flex items-center gap-1">
                <Globe className="w-3 h-3 text-indigo-500" />
                <span>{translate(currentLang, 'language', 'زبان')}</span>
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => handleSelectLanguage('fa')}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                    currentLang === 'fa'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  🇮🇷 فا
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectLanguage('en')}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                    currentLang === 'en'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  🇬🇧 EN
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectLanguage('sv')}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                    currentLang === 'sv'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  🇸🇪 SV
                </button>
              </div>
            </div>

            {/* Categorized Collapsible Links List */}
            <nav className="space-y-2.5">
              {menuCategories.map((cat, idx) => {
                const isExpanded = !!expandedCategories[idx];
                const hasActiveChild = cat.items.some(item => item.id === activeTab);

                return (
                  <div key={idx} className="rounded-2xl border border-gray-100 dark:border-gray-800/80 bg-gray-50/50 dark:bg-gray-800/20 overflow-hidden transition-all">
                    {/* Accordion Category Header */}
                    <button
                      type="button"
                      onClick={() => setExpandedCategories(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      className={`w-full flex items-center justify-between px-3 py-2 text-[11px] font-extrabold transition-colors cursor-pointer ${
                        hasActiveChild 
                          ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/60 dark:bg-indigo-950/40' 
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/60 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Folder className={`w-3.5 h-3.5 ${hasActiveChild ? 'text-indigo-500' : 'text-gray-400'}`} />
                        <span>{cat.title}</span>
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-indigo-500' : 'text-gray-400'}`} />
                    </button>

                    {/* Submenu Items */}
                    {isExpanded && (
                      <div className="px-1.5 py-1 space-y-0.5 border-t border-gray-100/60 dark:border-gray-800/50">
                        {cat.items.map((item) => {
                          const Icon = item.icon;
                          const isSelected = activeTab === item.id;
                          
                          return (
                            <button
                              key={item.id}
                              onClick={() => setActiveTab(item.id)}
                              className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                                isSelected 
                                  ? 'bg-indigo-600 text-white font-bold shadow-xs' 
                                  : 'text-gray-600 hover:text-gray-950 dark:text-gray-300 dark:hover:text-white hover:bg-white dark:hover:bg-gray-800/60'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : item.color}`} />
                                <span>{item.label}</span>
                              </div>
                              {isSelected && <ChevronRight className="w-3.5 h-3.5 rotate-180" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

          </div>

          {/* User Profile bar */}
          {user && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 px-1.5 space-y-2.5">
              <div className="flex items-center gap-2.5">
                <UserAvatar
                  photoURL={user.photoURL}
                  photo_url={user.photo_url}
                  displayName={user.displayName}
                  email={user.email}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-bold text-gray-900 dark:text-white block truncate">
                    {user.displayName && user.displayName !== translate(currentLang, 'defaultUserApp') && user.displayName !== translate(currentLang, 'appShortName') ? user.displayName : user.email?.split('@')[0] || translate(currentLang, 'defaultUser')}
                  </span>
                  <span className="text-[9px] text-gray-400 block truncate font-mono leading-none mt-0.5">
                    {user.email}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setStoredUser(null);
                  setUser(null);
                }}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 border border-red-100 dark:border-red-950/30 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 rounded-lg text-[10px] font-bold transition-colors cursor-pointer"
              >
                <LogOut className="w-3.5 h-3.5" />
                {translate(currentLang, 'appLogoutMenu')}
              </button>
            </div>
          )}

          {/* Footer details */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-4 text-center px-1">
            <div className="flex items-center justify-center gap-1.5">
              <span className="text-[10px] text-gray-500 font-bold">{translate(currentLang, 'appShortName')}</span>
              <button
                onClick={() => {
                  if (isAdmin) setShowVersionModal(true);
                }}
                className={`text-[9px] font-bold font-mono px-2 py-1 rounded-md transition-all ${
                  isAdmin 
                    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 hover:bg-indigo-100 cursor-pointer' 
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                }`}
                title={isAdmin ? translate(currentLang, 'appAdminVersionList', 'مشاهده لیست تغییرات نسخه‌ها (مخصوص مدیریت)') : translate(currentLang, 'appVersionPrefix', '', { version: CURRENT_VERSION })}
              >
                {translate(currentLang, 'appVersionOnly', '', { version: CURRENT_VERSION })}
              </button>
            </div>
            <span className="text-[9px] text-gray-400 block font-mono mt-1">{translate(currentLang, 'appCloudDbDesc')}</span>
          </div>
        </aside>

        {/* Mobile Navigation Drawer Overlay (Right Side) */}
        {mobileMenuOpen && (
          <div 
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden fixed inset-0 z-50 bg-gray-950/50 dark:bg-gray-950/70 backdrop-blur-xs transition-opacity"
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-0 bottom-0 w-4/5 max-w-xs bg-white dark:bg-gray-900 h-full p-5 ios-safe-drawer-top ios-safe-drawer-bottom shadow-2xl flex flex-col justify-between overflow-y-auto animate-in slide-in-from-right duration-200"
            >
              <div className="space-y-5">
                <div className="flex justify-between items-center border-b pb-4 border-gray-100 dark:border-gray-800">
                  <span className="font-extrabold text-base font-display text-gray-900 dark:text-white">{translate(currentLang, 'appMobileMenuTitle')}</span>
                  <button 
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <nav className="space-y-2.5">
                  {menuCategories.map((cat, idx) => {
                    const isExpanded = !!expandedCategories[idx];
                    const hasActiveChild = cat.items.some(item => item.id === activeTab);

                    return (
                      <div key={idx} className="rounded-2xl border border-gray-100 dark:border-gray-800/80 bg-gray-50/50 dark:bg-gray-800/20 overflow-hidden transition-all">
                        {/* Accordion Category Header */}
                        <button
                          type="button"
                          onClick={() => setExpandedCategories(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-extrabold transition-colors cursor-pointer ${
                            hasActiveChild 
                              ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/60 dark:bg-indigo-950/40' 
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Folder className={`w-3.5 h-3.5 ${hasActiveChild ? 'text-indigo-500' : 'text-gray-400'}`} />
                            <span>{cat.title}</span>
                          </div>
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-indigo-500' : 'text-gray-400'}`} />
                        </button>

                        {/* Submenu Items */}
                        {isExpanded && (
                          <div className="px-1.5 py-1 space-y-0.5 border-t border-gray-100/60 dark:border-gray-800/50">
                            {cat.items.map((item) => {
                              const Icon = item.icon;
                              const isSelected = activeTab === item.id;
                              
                              return (
                                <button
                                  key={item.id}
                                  onClick={() => {
                                    setActiveTab(item.id);
                                    setMobileMenuOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                                    isSelected 
                                      ? 'bg-indigo-600 text-white font-bold shadow-xs' 
                                      : 'text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800'
                                  }`}
                                >
                                  <div className="flex items-center gap-2.5">
                                    <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : item.color}`} />
                                    <span>{item.label}</span>
                                  </div>
                                  {isSelected && <ChevronRight className="w-4 h-4 rotate-180" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>

              {/* User Profile Mobile and Logout */}
              {user && (
                <div className="border-t border-gray-100 dark:border-gray-800 pt-4 px-1.5 space-y-2">
                  <div className="flex items-center gap-2.5">
                    <UserAvatar
                      photoURL={user.photoURL}
                      photo_url={user.photo_url}
                      displayName={user.displayName}
                      email={user.email}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-bold text-gray-900 dark:text-white block truncate">
                        {user.displayName && user.displayName !== translate(currentLang, 'defaultUserApp') && user.displayName !== translate(currentLang, 'appShortName') ? user.displayName : user.email?.split('@')[0] || translate(currentLang, 'defaultUser')}
                      </span>
                      <span className="text-[9px] text-gray-400 block truncate font-mono">
                        {user.email}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStoredUser(null);
                      setUser(null);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-center gap-1.5 py-2 border border-red-100 dark:border-red-950/30 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    {translate(currentLang, 'appLogoutMenu')}
                  </button>
                </div>
              )}

              <div className="border-t border-gray-100 dark:border-gray-800 pt-4 text-center text-[10px]">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-gray-500 font-bold">{translate(currentLang, 'appShortName')}</span>
                  <button
                    onClick={() => {
                      if (isAdmin) {
                        setMobileMenuOpen(false);
                        setShowVersionModal(true);
                      }
                    }}
                    className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${
                      isAdmin 
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 font-bold cursor-pointer hover:bg-indigo-100' 
                        : 'text-gray-400'
                    }`}
                  >
                    {translate(currentLang, 'appVersionOnly', '', { version: CURRENT_VERSION })}
                  </button>
                </div>
                <span className="text-gray-400 block mt-1">{translate(currentLang, 'appSlogan')}</span>
                <span className="text-[9px] text-gray-500 block font-mono mt-1 opacity-70">App ID: {user.id || user.uid || '---'}</span>
              </div>

            </div>
          </div>
        )}

        {/* Content Area Canvas with safe-padding for mobile bottom navigation */}
        <main className="flex-1 w-full min-w-0 px-4 py-6 pb-28 md:px-8 lg:pb-8 max-w-7xl mx-auto">
          {renderActiveContent()}
        </main>

        {/* Mobile Sticky Bottom Tab Bar */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 dark:bg-gray-900/95 border-t border-gray-100 dark:border-gray-800/80 shadow-[0_-4px_12px_rgba(0,0,0,0.03)] ios-safe-bottom-nav px-2 py-2 backdrop-blur-md flex justify-around items-center">
          {/* Dashboard tab */}
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`flex flex-col items-center gap-1 py-1 px-2.5 rounded-xl transition-all cursor-pointer ${
              activeTab === 'dashboard'
                ? 'text-indigo-600 dark:text-indigo-400 font-bold scale-105'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <Home className="w-5 h-5 mb-0.5" />
            <span className="text-[9px] font-medium">{translate(currentLang, 'navDashboard')}</span>
          </button>

          {/* Nutrition tab */}
          <button 
            onClick={() => setActiveTab('nutrition')}
            className={`flex flex-col items-center gap-1 py-1 px-2.5 rounded-xl transition-all cursor-pointer ${
              activeTab === 'nutrition'
                ? 'text-emerald-600 dark:text-emerald-400 font-bold scale-105'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <Utensils className="w-5 h-5 mb-0.5" />
            <span className="text-[9px] font-medium">{translate(currentLang, 'navNutrition')}</span>
          </button>

          {/* Reminders Center tab */}
          <button 
            onClick={() => setActiveTab('reminders')}
            className={`flex flex-col items-center gap-1 py-1 px-2.5 rounded-xl transition-all cursor-pointer ${
              activeTab === 'reminders'
                ? 'text-rose-600 dark:text-rose-400 font-bold scale-105'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <Bell className="w-5 h-5 mb-0.5" />
            <span className="text-[9px] font-medium">{translate(currentLang, 'navReminders')}</span>
          </button>

          {/* Spine Rehab tab */}
          <button 
            onClick={() => setActiveTab('rehab')}
            className={`flex flex-col items-center gap-1 py-1 px-2.5 rounded-xl transition-all cursor-pointer ${
              activeTab === 'rehab'
                ? 'text-emerald-600 dark:text-emerald-400 font-bold scale-105'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <Activity className="w-5 h-5 mb-0.5" />
            <span className="text-[9px] font-medium">{translate(currentLang, 'navSport')}</span>
          </button>

          {/* More Menu drawer button */}
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className="flex flex-col items-center gap-1 py-1 px-2.5 rounded-xl transition-all text-gray-400 dark:text-gray-500 cursor-pointer hover:text-indigo-600"
          >
            <Menu className="w-5 h-5 mb-0.5" />
            <span className="text-[9px] font-medium">{translate(currentLang, 'navMenu')}</span>
          </button>
        </nav>

      </div>

      {/* Version History Modal (Only accessible for salim.zarepour@gmail.com) */}
      <VersionHistoryModal 
        isOpen={showVersionModal} 
        onClose={() => setShowVersionModal(false)} 
        userEmail={user?.email} 
      />

      </div>
    </LanguageContext.Provider>
  );
}
